import { useEffect, type ReactNode } from "react";
import { useAtomValue } from "jotai";

import { MARQUE } from "@dozka/ui";
import { brandColorAtom, tailleAffichageAtom, themeAtom } from "@/core/state";
import { applyBrandColor } from "@dozka/ui";
import {
  appliquerTailleAffichage,
  resoudreTaille,
} from "@/core/theme/tailleAffichage";

/**
 * Reads the current theme atom (persisted in localStorage) and toggles
 * the `.dark` class on <html>. All TailAdmin components follow this class.
 *
 * Applique aussi la couleur de marque personnalisée (`brandColorAtom`) en CSS
 * vars inline sur <html> → recolore tout le cockpit (`*-mark`) en direct.
 */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const theme = useAtomValue(themeAtom);
  // Pas de choix de l'utilisateur ⇒ l'accent de la marque du domaine (l'or de
  // RishFans) ; `null` chez Dozka = le bleu du thème, comme avant.
  const brandColor = useAtomValue(brandColorAtom) ?? MARQUE.accent;
  const tailleAffichage = useAtomValue(tailleAffichageAtom);

  // ⚡ Mode et accent posés PENDANT le rendu, pas dans un effet. Un effet
  // s'exécute APRÈS le rendu des enfants : un graphe qui lit `--color-mark`
  // dans son `useMemo` lisait donc la couleur d'AVANT, et le cockpit ne
  // changeait pas « partout d'un coup » (Kayla, 07-10-2026). Le parent rend
  // avant ses enfants : poser ici garantit que tout ce qui se recalcule dans ce
  // même passage lit les nouvelles valeurs. Écritures idempotentes sur <html>.
  if (typeof document !== "undefined") {
    appliquerLeMode(document.documentElement, theme);
    applyBrandColor(document.documentElement, brandColor);
  }

  // Taille d'affichage — `zoom` sur <body>. Sur <html> il est calcule et sans
  // aucun effet (mesure Chrome 148) ; cf core/theme/tailleAffichage.ts.
  //
  // On re-applique au redimensionnement PARCE QUE le mode auto se calcule sur
  // `devicePixelRatio` — et que Chrome emet `resize` quand il bouge : changement
  // de zoom navigateur, ou fenetre glissee sur un second ecran a une autre
  // echelle. Les deux reglages ne se battent donc pas : le notre se recale sur
  // ce que l'autre vient de decider.
  useEffect(() => {
    const appliquer = () =>
      appliquerTailleAffichage(document.body, resoudreTaille(tailleAffichage));
    appliquer();
    if (tailleAffichage !== "auto") return;
    let attente = 0;
    const surResize = () => {
      window.clearTimeout(attente);
      attente = window.setTimeout(appliquer, 120);
    };
    window.addEventListener("resize", surResize);
    return () => {
      window.clearTimeout(attente);
      window.removeEventListener("resize", surResize);
    };
  }, [tailleAffichage]);

  return <>{children}</>;
}

/** "glass" est une variante DARK translucide : on garde `.dark` pour que les
 *  composants TailAdmin (qui suivent `.dark`) restent en sombre, et on pose
 *  EN PLUS `data-theme="glass"` qui re-mappe les surfaces en verre dépoli
 *  (cf globals.css). "light" = ni l'un ni l'autre. */
function appliquerLeMode(root: HTMLElement, theme: "light" | "dark" | "glass"): void {
  root.classList.toggle("dark", theme === "dark" || theme === "glass");
  if (theme === "glass") root.setAttribute("data-theme", "glass");
  else root.removeAttribute("data-theme");
}
